import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, Edit2, Trash2, Search, ExternalLink } from 'lucide-react';
import api from '../../api';

export default function AdminPartners() {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    useEffect(() => {
        loadPartners();
    }, []);

    const loadPartners = async () => {
        try {
            const data = await api.getPartners({ limit: 100 });
            setPartners(data);
        } catch (error) {
            console.error('Error loading partners:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (partnerId) => {
        try {
            await api.deletePartner(partnerId);
            setPartners(partners.filter(p => p.id !== partnerId));
        } catch (error) {
            alert('Error deleting partner: ' + error.message);
        }
    };

    const handleImportClick = () => {
        document.getElementById('import-file').click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const json = JSON.parse(e.target.result);
                // Validate if array
                if (!Array.isArray(json)) {
                    throw new Error('File content must be a JSON array');
                }

                if (!window.confirm(`Ready to import ${json.length} partners?`)) return;

                setLoading(true);
                const result = await api.importPartners(json);

                let msg = result.message || 'Import processed';
                if (result.skipped_count > 0) {
                    msg += `\n\nSkipped ${result.skipped_count} duplicates:\n- ${result.skipped_items.join('\n- ')}`;
                }

                if (result.errors && result.errors.length > 0) {
                    msg += `\n\nErrors:\n${result.errors.join('\n')}`;
                }

                alert(msg);
                loadPartners();
            } catch (error) {
                console.error('Import failed:', error);
                alert('Import failed: ' + error.message);
            } finally {
                setLoading(false);
                event.target.value = ''; // Reset input
            }
        };
        reader.readAsText(file);
    };

    const downloadSample = () => {
        const sample = [
            {
                "name": "Sample Partner",
                "category": "Dance",
                "description": "A description of the partner",
                "email": "contact@example.com",
                "phone": "+123456789",
                "website": "https://example.com",
                "contact_person": "Jane Doe",
                "role": "Director",
                "artforms": ["Salsa", "Bachata"],
                "services": ["Classes", "Workshops"]
            }
        ];
        const blob = new Blob([JSON.stringify(sample, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'partners_sample.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const categories = [...new Set(partners.map(p => p.category).filter(Boolean))];
    const filteredPartners = partners.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
            p.description?.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = !categoryFilter || p.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div>
                    <h1 className="admin-page-title" style={{ marginBottom: '8px' }}>Partners Management</h1>
                    <p style={{ color: 'var(--v2-text-muted)' }}>
                        Manage platform partners and categories
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                        type="file"
                        id="import-file"
                        accept=".json"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Link 
                        to="/admin/partners/create" 
                        style={{ 
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '10px 20px', borderRadius: '12px',
                            background: 'var(--v2-primary)', border: 'none',
                            color: 'white', fontWeight: '600', fontSize: '14px',
                            cursor: 'pointer', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                            textDecoration: 'none'
                        }}
                    >
                        <Plus size={18} /> Add Partner
                    </Link>
                </div>
            </div>

            {/* Stats */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                marginBottom: '32px'
            }}>
                <div className="admin-card-premium" style={{ padding: '24px' }}>
                    <div style={{ color: 'var(--v2-text-muted)', marginBottom: '8px', fontSize: '14px' }}>Total Partners</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--v2-primary)' }}>
                        {partners.length}
                    </div>
                </div>
                <div className="admin-card-premium" style={{ padding: '24px' }}>
                    <div style={{ color: 'var(--v2-text-muted)', marginBottom: '8px', fontSize: '14px' }}>Categories</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#6418f3' }}>
                        {categories.length}
                    </div>
                </div>
                <div className="admin-card-premium" style={{ padding: '24px' }}>
                    <div style={{ color: 'var(--v2-text-muted)', marginBottom: '8px', fontSize: '14px' }}>Dance Partners</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--v2-primary)' }}>
                        {partners.filter(p => p.category === 'Dance').length}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="admin-search-wrapper" style={{ maxWidth: '600px', marginBottom: '32px' }}>
                <Search size={18} className="admin-search-icon" />
                <input
                    type="text"
                    placeholder="Search partners by name or description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="admin-search-input"
                />
                <div style={{ height: '24px', width: '1px', background: 'var(--v2-border)', margin: '0 8px' }} />
                <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    style={{ 
                        border: 'none', 
                        outline: 'none', 
                        fontSize: '14px', 
                        fontWeight: '600',
                        color: 'var(--v2-text-main)',
                        background: 'transparent',
                        cursor: 'pointer'
                    }}
                >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
            </div>

            {/* Partners Table */}
            {loading ? (
                <div className="admin-card-premium" style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '40px', height: '40px', border: '3px solid var(--v2-border)', borderTopColor: 'var(--v2-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                </div>
            ) : (
                <div className="admin-table-container">
                    <table className="admin-table-premium">
                        <thead>
                            <tr>
                                <th>Partner</th>
                                <th>Category</th>
                                <th>Contact Details</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPartners.map((partner) => (
                                <tr key={partner.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                            <div style={{
                                                width: '48px',
                                                height: '48px',
                                                borderRadius: '12px',
                                                background: 'var(--v2-primary-soft)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'var(--v2-primary)',
                                                fontWeight: '800',
                                                fontSize: '18px'
                                            }}>
                                                {partner.name.charAt(0)}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--v2-text-main)' }}>{partner.name}</div>
                                                <div style={{ fontSize: '13px', color: 'var(--v2-text-muted)', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {partner.description || 'No description provided'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="status-tag category">
                                            {partner.category || 'Uncategorized'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--v2-text-main)' }}>{partner.email || 'N/A'}</div>
                                            <div style={{ fontSize: '12px', color: 'var(--v2-text-muted)' }}>{partner.phone || 'N/A'}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-actions">
                                            <Link
                                                to={`/admin/partners/${partner.id}`}
                                                className="table-action-btn edit"
                                                title="View Details"
                                            >
                                                <ExternalLink size={18} />
                                            </Link>
                                            <Link
                                                to={`/admin/partners/edit/${partner.id}`}
                                                className="table-action-btn edit"
                                                title="Edit Partner"
                                            >
                                                <Edit2 size={18} />
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(partner.id)}
                                                className="table-action-btn delete"
                                                title="Delete Partner"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {filteredPartners.length === 0 && !loading && (
                <div className="admin-card-premium" style={{ padding: '48px', textAlign: 'center' }}>
                    <Building2 size={48} style={{ color: 'var(--v2-text-muted)', marginBottom: '16px', margin: '0 auto' }} />
                    <h3 style={{ marginBottom: '8px', color: 'var(--v2-text-main)', fontSize: '18px', fontWeight: '700' }}>No partners found</h3>
                    <p style={{ color: 'var(--v2-text-muted)' }}>
                        {search || categoryFilter ? 'Try adjusting your filters' : 'Add your first partner to get started'}
                    </p>
                </div>
            )}
        </div>
    );
}
